/**
 * useTheme — read/write the four curated themes + custom theme generator hook.
 *
 * Design.md §5.1 (four themes), §5.2 (custom generator), §5.3 (persistence).
 *
 * localStorage keys (prefix is proof-*, never instrument-* — CLAUDE.md §5):
 *   proof-theme      — "ember" | "forest" | "cobalt" | "clay" | "custom"
 *   proof-custom-theme — JSON { name, lightAccent, darkAccent }
 *   proof-appearance — "light" | "dark" | "system"
 *
 * The internal aesthetic direction name "Instrument" never appears here.
 *
 * Theme is applied as a class on document.documentElement:
 *   .theme-ember | .theme-forest | .theme-cobalt | .theme-clay
 *
 * Appearance is applied as .light | .dark (absent = system via CSS media query).
 */

import { useState, useEffect, useCallback } from "react";

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_THEME = "proof-theme" as const;
const STORAGE_CUSTOM_THEME = "proof-custom-theme" as const;
// STILL THE LIVE WRITE KEY. setAppearance() persists the user's choice here even
// while dark mode is locked — readAppearance() returns "light" regardless, but
// the stored value is the post-MVP dark-mode-revival persistence path. A future
// cleanup MUST NOT delete this key (doing so silently drops a saved preference
// that revival would otherwise honour).
const STORAGE_APPEARANCE = "proof-appearance" as const;

export type ThemeName = "ember" | "forest" | "cobalt" | "clay" | "custom";
export type Appearance = "light" | "dark" | "system";

export interface CustomTheme {
  name: string;
  lightAccent: string; // 6-digit hex
  darkAccent: string; // 6-digit hex
}

const VALID_THEMES = new Set<ThemeName>(["ember", "forest", "cobalt", "clay", "custom"]);
// VALID_APPEARANCES intentionally omitted — readAppearance() is locked to 'light'
// post-MVP and no longer validates a stored value. Restore alongside the lock
// removal in readAppearance() when dark mode is revived.
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function readTheme(): ThemeName {
  try {
    const raw = localStorage.getItem(STORAGE_THEME);
    if (raw && VALID_THEMES.has(raw as ThemeName)) return raw as ThemeName;
    // eslint-disable-next-line no-restricted-syntax -- best-effort: theme UI preference read; unavailable storage falls back to the "ember" default below (Rule 8 documented degradation).
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
  return "ember";
}

/**
 * @internal — exported for unit testing only. Do not call from application
 * code; use the `appearance` value returned by `useTheme()` instead.
 *
 * Dark mode deferred post-MVP — locked to light regardless of stored or
 * system preference. The Appearance type + setAppearance plumbing are
 * preserved so dark mode can be revived without structural changes.
 * DO NOT read localStorage or matchMedia here until the lock is lifted.
 */
export function readAppearance(): Appearance {
  return "light";
}

function readCustomTheme(): CustomTheme | null {
  try {
    const raw = localStorage.getItem(STORAGE_CUSTOM_THEME);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "name" in parsed &&
      "lightAccent" in parsed &&
      "darkAccent" in parsed
    ) {
      const p = parsed as Record<string, unknown>;
      if (
        typeof p["name"] === "string" &&
        HEX_RE.test(String(p["lightAccent"])) &&
        HEX_RE.test(String(p["darkAccent"]))
      ) {
        return parsed as CustomTheme;
      }
    }
    // eslint-disable-next-line no-restricted-syntax -- best-effort: parses a stored custom-theme preference; malformed JSON / unavailable storage returns null (no custom theme) below (Rule 8 documented degradation).
  } catch {
    // malformed JSON — ignore
  }
  return null;
}

/** Apply theme class and custom accent CSS property to the root element. */
function applyTheme(
  theme: ThemeName,
  appearance: Appearance,
  customTheme: CustomTheme | null,
): void {
  const root = document.documentElement;

  // Remove all theme classes, then set the active one.
  root.classList.remove("theme-ember", "theme-forest", "theme-cobalt", "theme-clay");
  root.classList.add(`theme-${theme}`);

  // Apply appearance classes.
  root.classList.remove("light", "dark");
  if (appearance === "light") root.classList.add("light");
  if (appearance === "dark") root.classList.add("dark");

  // For custom theme: inject the accent as a CSS custom property override.
  if (theme === "custom" && customTheme) {
    const isDark =
      appearance === "dark" ||
      (appearance === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    root.style.setProperty("--accent", isDark ? customTheme.darkAccent : customTheme.lightAccent);
  } else {
    // Clear any previously-set inline override.
    root.style.removeProperty("--accent");
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseThemeReturn {
  /** Active accent theme name. */
  theme: ThemeName;
  /** Active appearance mode. */
  appearance: Appearance;
  /** The saved custom theme, or null if none exists. */
  customTheme: CustomTheme | null;
  /** Set the active accent theme. */
  setTheme: (theme: ThemeName) => void;
  /** Set light/dark/system preference. */
  setAppearance: (appearance: Appearance) => void;
  /**
   * Save a custom theme and optionally activate it immediately.
   * Validation: lightAccent and darkAccent must be 6-digit hex strings.
   * Returns true on success, false if validation fails.
   */
  saveCustomTheme: (custom: CustomTheme, activate?: boolean) => boolean;
}

export function useTheme(): UseThemeReturn {
  const [theme, setThemeState] = useState<ThemeName>(readTheme);
  const [appearance, setAppearanceState] = useState<Appearance>(readAppearance);
  const [customTheme, setCustomThemeState] = useState<CustomTheme | null>(readCustomTheme);

  // Apply on mount and whenever state changes.
  useEffect(() => {
    applyTheme(theme, appearance, customTheme);
  }, [theme, appearance, customTheme]);

  // System colour-scheme listener — inert while dark mode is deferred (post-MVP
  // lock to light). readAppearance() always returns "light", so this branch
  // never fires. Kept structurally for revival; re-enable by removing the lock
  // in readAppearance() above and restoring the condition below.
  useEffect(() => {
    // appearance is always "light" while dark mode is locked; skip subscription.
    if (appearance !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme(theme, appearance, customTheme);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme, appearance, customTheme]);

  const setTheme = useCallback((next: ThemeName) => {
    try {
      localStorage.setItem(STORAGE_THEME, next);
      // eslint-disable-next-line no-restricted-syntax -- best-effort: persists the theme UI preference, not user data; unavailable storage is a no-op and in-memory state still updates (Rule 8 documented degradation).
    } catch {
      // localStorage unavailable — state still updates
    }
    setThemeState(next);
  }, []);

  const setAppearance = useCallback((next: Appearance) => {
    try {
      localStorage.setItem(STORAGE_APPEARANCE, next);
      // eslint-disable-next-line no-restricted-syntax -- best-effort: persists the appearance UI preference, not user data; unavailable storage is a no-op and in-memory state still updates (Rule 8 documented degradation).
    } catch {
      // localStorage unavailable
    }
    setAppearanceState(next);
  }, []);

  const saveCustomTheme = useCallback((custom: CustomTheme, activate = true): boolean => {
    if (!HEX_RE.test(custom.lightAccent) || !HEX_RE.test(custom.darkAccent)) {
      return false;
    }
    try {
      localStorage.setItem(STORAGE_CUSTOM_THEME, JSON.stringify(custom));
      // eslint-disable-next-line no-restricted-syntax -- best-effort: persists the custom-theme UI preference, not user data; unavailable storage is a no-op and in-memory state still updates (Rule 8 documented degradation).
    } catch {
      // localStorage unavailable
    }
    setCustomThemeState(custom);
    if (activate) {
      try {
        localStorage.setItem(STORAGE_THEME, "custom");
        // eslint-disable-next-line no-restricted-syntax -- best-effort: persists the active-theme UI preference, not user data; unavailable storage is a no-op and in-memory state still updates (Rule 8 documented degradation).
      } catch {
        // localStorage unavailable
      }
      setThemeState("custom");
    }
    return true;
  }, []);

  return { theme, appearance, customTheme, setTheme, setAppearance, saveCustomTheme };
}

/** Initialise theme from localStorage before first React paint to avoid flash. */
export function initTheme(): void {
  const theme = readTheme();
  const appearance = readAppearance();
  const customTheme = readCustomTheme();
  applyTheme(theme, appearance, customTheme);
}
